import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface TicketSocketPayload {
  document_id: number;
  caisse_id?: number | null;
  station_id?: number | null;
  entrepot_id?: number | null;
  code_document?: string | null;
  type?: string | null;
  reglement?: string | null;
  statut?: string | null;
  total?: number | null;
  sous_type?: string | null;
  cloture?: string | null;
  passed_at?: string | null;
  passed_by_user_id?: number | null;
  /** true si le document a été annulé / retiré du panier caisse */
  cancelled?: boolean;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private authSub?: Subscription;
  private connectingToken: string | null = null;

  /** Événements ticket (création, MAJ, passation, annulation) — payload pour upsert local. */
  readonly ticketUpdated$ = new Subject<TicketSocketPayload>();

  constructor(private readonly authService: LogicielCaisseAuthService) {
    this.authSub = this.authService.authStatus$.subscribe((status) => {
      if (status === 'ready') {
        this.connect();
      } else if (status === 'idle') {
        this.disconnect();
      }
    });
  }

  connect(): void {
    const token = this.authService.getLogicielCaisseToken();
    if (!token) {
      return;
    }
    if (this.socket?.connected && this.connectingToken === token) {
      return;
    }
    this.disconnect();
    this.connectingToken = token;
    const base = this.authService.apiBase().replace(/\/$/, '');
    this.socket = io(base, {
      auth: { token },
      path: '/socket.io',
      /** WebSocket d’abord, polling en fallback (bonne pratique Socket.IO). */
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1500,
      timeout: 20000,
    });
    this.bindTicketListeners(this.socket);
  }

  private bindTicketListeners(socket: Socket): void {
    const forward = (payload: TicketSocketPayload, cancelled = false) => {
      if (payload?.document_id == null) {
        return;
      }
      this.ticketUpdated$.next({
        ...payload,
        cancelled: cancelled || payload.cancelled === true,
      });
    };
    socket.on('ticket:updated', (p: TicketSocketPayload) => forward(p, false));
    socket.on('ticket:passation', (p: TicketSocketPayload) => forward(p, false));
    socket.on('ticket:cancelled', (p: TicketSocketPayload) => forward(p, true));
  }

  /** Ne pas appeler depuis chaque page : le cycle de vie suit authStatus$. */
  disconnect(): void {
    this.connectingToken = null;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.disconnect();
    this.ticketUpdated$.complete();
  }
}
